'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import {
  generateICS,
  generateCSV,
  getGoogleCalendarUrl,
  exportCalendarPNG,
  downloadBlob,
} from '@/lib/calendar-export';
import type { CalendarEvent } from '@/lib/types';

/* ── Icons ────────────────────────────────────────────────────────── */

function ExportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function GoogleCalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function TableIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function FileTextIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* ── Types ────────────────────────────────────────────────────────── */

interface CalendarExportMenuProps {
  events: CalendarEvent[];
  title: string;
  countryCode?: string;
}

/* ── Component ───────────────────────────────────────────────────── */

export function CalendarExportMenu({ events, title, countryCode }: CalendarExportMenuProps) {
  const lang = useCalendarStore((s) => s.lang);
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const slug = countryCode?.toLowerCase() || 'calendar';

  const handleICS = useCallback(() => {
    const ics = generateICS(events, title, countryCode);
    downloadBlob(ics, `${slug}-${new Date().getFullYear()}.ics`, 'text/calendar');
    window._paq?.push(['trackEvent', 'Export', 'ICS', countryCode || 'unknown']);
    setOpen(false);
  }, [events, title, countryCode, slug]);

  const handleGoogleCalendar = useCallback(() => {
    if (events.length === 1) {
      // Single event: open Google Calendar intent URL directly
      window.open(getGoogleCalendarUrl(events[0]), '_blank');
    } else {
      // Bulk: download ICS (Google Calendar doesn't support bulk intent URLs)
      const ics = generateICS(events, title, countryCode);
      downloadBlob(ics, `${slug}-${new Date().getFullYear()}.ics`, 'text/calendar');
    }
    window._paq?.push(['trackEvent', 'Export', 'GoogleCalendar', countryCode || 'unknown']);
    setOpen(false);
  }, [events, title, countryCode, slug]);

  const handleCSV = useCallback(() => {
    const csv = generateCSV(events);
    downloadBlob(csv, `${slug}-${new Date().getFullYear()}.csv`, 'text/csv');
    window._paq?.push(['trackEvent', 'Export', 'CSV', countryCode || 'unknown']);
    setOpen(false);
  }, [events, countryCode, slug]);

  const handlePNG = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportCalendarPNG(countryCode);
    } finally {
      setExporting(false);
      setOpen(false);
    }
  }, [countryCode, exporting]);

  const handlePDF = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { generatePDF } = await import('@/lib/calendar-export-pdf');
      await generatePDF(events, title, countryCode);
      window._paq?.push(['trackEvent', 'Export', 'PDF', countryCode || 'unknown']);
    } finally {
      setExporting(false);
      setOpen(false);
    }
  }, [events, title, countryCode, exporting]);

  const labelExport = uiFb('ui.export', 'Export', lang);

  const menuItems = [
    { icon: <CalendarIcon />, label: uiFb('ui.export_ics', 'Add to Calendar (.ics)', lang), onClick: handleICS },
    { icon: <GoogleCalIcon />, label: uiFb('ui.export_gcal', 'Google Calendar (.ics)', lang), onClick: handleGoogleCalendar },
    { icon: <FileTextIcon />, label: uiFb('ui.export_pdf', 'Download PDF', lang), onClick: handlePDF },
    { icon: <TableIcon />, label: uiFb('ui.export_csv', 'Download CSV', lang), onClick: handleCSV },
    { icon: <ImageIcon />, label: uiFb('ui.export_png', 'Save as Image', lang), onClick: handlePNG },
  ];

  /* ── Dropdown renderer (mobile / compact) ── */
  const renderDropdown = (wrapperClassName?: string) => (
    <div className={`relative ${wrapperClassName || ''}`} ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors"
        aria-label={labelExport}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <ExportIcon />
        <span>{labelExport}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[200px] content-card p-1 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
        >
          {menuItems.map((item) => (
            <button
              key={item.label}
              role="menuitem"
              onClick={item.onClick}
              disabled={exporting}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-surface-hover hover:text-text-primary transition-colors disabled:opacity-50"
            >
              <span className="shrink-0 text-text-tertiary">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return renderDropdown();
}
